/**
 * List pricing for the Claude models this application invokes, and the
 * conversion from the token ledger (`llm_task_usage`) to dollars.
 *
 * Why this exists: every spend guard in the matching pipeline was
 * token-denominated, but the operator budgeting a run thinks in dollars and
 * watches a dollar-denominated billing dashboard. This table is the single
 * bridge between the two. Budgets, pre-flight estimates, step outputs and any
 * UI cost display must all convert through here so one rate change updates
 * every surface at once.
 *
 * Rates are Anthropic LIST prices in USD per MILLION tokens (verified against
 * the Anthropic model catalog on 2026-08-20). Where an introductory discount
 * exists (Sonnet 5 through 2026-08-31) we deliberately keep the standard
 * sticker rate: estimates and budgets should be conservative, and a budget
 * tuned against a temporary discount would silently loosen when it ends.
 */

export interface LlmModelPricingModel {
	/** Exact Anthropic model id as sent on the API request. */
	modelId: string;
	/** Human label for step-config model pickers. */
	label: string;
	/** USD per 1,000,000 input tokens. */
	inputUsdPerMTok: number;
	/** USD per 1,000,000 output tokens. */
	outputUsdPerMTok: number;
	/** USD per 1,000,000 cache-read input tokens (~0.1x input). */
	cacheReadUsdPerMTok: number;
	/** USD per 1,000,000 cache-write input tokens (1.25x input, 5m TTL). */
	cacheWriteUsdPerMTok: number;
}

/**
 * The hosted web_search surcharge: $10 per 1,000 searches, billed per
 * request regardless of model.
 */
export const WEB_SEARCH_USD_PER_REQUEST = 0.01;

/**
 * Models the product pipelines may run, with list rates. Order matters: step
 * config pickers render this order, so keep it cheapest-first.
 */
export const LLM_MODEL_PRICING: Array<LlmModelPricingModel> = [
	{
		modelId: 'claude-haiku-4-5',
		label: 'Claude Haiku 4.5 (fastest, lowest cost)',
		inputUsdPerMTok: 1,
		outputUsdPerMTok: 5,
		cacheReadUsdPerMTok: 0.1,
		cacheWriteUsdPerMTok: 1.25,
	},
	{
		modelId: 'claude-sonnet-5',
		label: 'Claude Sonnet 5 (balanced)',
		inputUsdPerMTok: 3,
		outputUsdPerMTok: 15,
		cacheReadUsdPerMTok: 0.3,
		cacheWriteUsdPerMTok: 3.75,
	},
	{
		modelId: 'claude-opus-5',
		label: 'Claude Opus 5 (highest capability)',
		inputUsdPerMTok: 5,
		outputUsdPerMTok: 25,
		cacheReadUsdPerMTok: 0.5,
		cacheWriteUsdPerMTok: 6.25,
	},
];

export interface LlmUsageForPricingModel {
	inputTokens: number;
	outputTokens: number;
	cacheReadInputTokens?: number | null;
	cacheCreationInputTokens?: number | null;
	webSearchRequests?: number | null;
}

export const findLlmModelPricing = (
	modelId: string | null | undefined,
): LlmModelPricingModel | null => {
	if (modelId == null || modelId === '') {
		return null;
	}

	// Date-suffixed ids (e.g. claude-haiku-4-5-20251001) bill at the alias
	// rate, so match on prefix after failing an exact match.
	const exact = LLM_MODEL_PRICING.find((entry) => entry.modelId === modelId);

	if (exact != null) {
		return exact;
	}

	return (
		LLM_MODEL_PRICING.find((entry) =>
			modelId.startsWith(`${entry.modelId}-`),
		) ?? null
	);
};

/**
 * Convert one ledger row's worth of usage to dollars, or null when the model
 * is unknown. Callers must surface the null (an "unpriceable" warning), never
 * treat it as zero — zero-cost is exactly the wrong failure mode for a
 * budgeting system.
 */
export const usageToUsd = (
	usage: LlmUsageForPricingModel,
	modelId: string | null | undefined,
): number | null => {
	const pricing = findLlmModelPricing(modelId);

	if (pricing == null) {
		return null;
	}

	const perTok = (tokens: number | null | undefined, usdPerMTok: number) =>
		((tokens ?? 0) * usdPerMTok) / 1_000_000;

	return (
		perTok(usage.inputTokens, pricing.inputUsdPerMTok) +
		perTok(usage.outputTokens, pricing.outputUsdPerMTok) +
		perTok(usage.cacheReadInputTokens, pricing.cacheReadUsdPerMTok) +
		perTok(usage.cacheCreationInputTokens, pricing.cacheWriteUsdPerMTok) +
		(usage.webSearchRequests ?? 0) * WEB_SEARCH_USD_PER_REQUEST
	);
};

/** Render a dollar amount for UI/step output: cents precision, floor at 1c shown as "<$0.01" when non-zero. */
export const formatUsdEstimate = (usd: number): string => {
	if (usd > 0 && usd < 0.01) {
		return '<$0.01';
	}

	return `$${usd.toFixed(2)}`;
};
